function parseTaxUUIDList(value) {
    if (typeof value !== 'string') {
        throw new Error('Tax relationship UUID list must be a string');
    }
    if (value === '') {
        return [];
    }
    return value.split(',').filter(taxUUID => taxUUID !== '');
}

function remapActiveTaxUUIDList(value, invoiceTaxUUIDMap) {
    return parseTaxUUIDList(value)
        .filter(settingTaxUUID => invoiceTaxUUIDMap.has(settingTaxUUID))
        .map(settingTaxUUID => invoiceTaxUUIDMap.get(settingTaxUUID))
        .join(',');
}

export function createInvoiceTaxInfoList(settingTaxInfoList, createUUID) {
    if (!Array.isArray(settingTaxInfoList)) {
        throw new Error('Setting tax list is required');
    }
    const invoiceTaxInfoList = JSON.parse(JSON.stringify(settingTaxInfoList));
    const invoiceTaxUUIDMap = new Map();

    for (let i = 0; i < invoiceTaxInfoList.length; i++) {
        const taxInfo = invoiceTaxInfoList[i];
        if (typeof taxInfo.innerUUID !== 'string' || taxInfo.innerUUID === '') {
            throw new Error('Setting tax UUID is required');
        }
        const invoiceTaxUUID = createUUID();
        invoiceTaxUUIDMap.set(taxInfo.innerUUID, invoiceTaxUUID);
        taxInfo.settingTaxInfoUUID = taxInfo.innerUUID;
        taxInfo.newInnerUUID = invoiceTaxUUID;
        taxInfo.fromClient = 3;
        taxInfo.belongToSetting = 0;
    }

    for (let i = 0; i < invoiceTaxInfoList.length; i++) {
        const taxInfo = invoiceTaxInfoList[i];
        taxInfo.accumulateTaxInfoUUIDArrayKey = remapActiveTaxUUIDList(
            taxInfo.accumulateTaxInfoUUIDArrayKey,
            invoiceTaxUUIDMap
        );
        taxInfo.beAccumulateTaxInfoUUIDArrayKey = remapActiveTaxUUIDList(
            taxInfo.beAccumulateTaxInfoUUIDArrayKey,
            invoiceTaxUUIDMap
        );
    }

    return invoiceTaxInfoList;
}

export function buildTaxCalculationFactorMap(invoiceTaxInfoList, itemTaxInfoList) {
    if (!Array.isArray(invoiceTaxInfoList) || !Array.isArray(itemTaxInfoList)) {
        throw new Error('Invoice tax list and item tax list are required');
    }

    const invoiceTaxInfoMap = new Map();
    for (let i = 0; i < invoiceTaxInfoList.length; i++) {
        const taxInfo = invoiceTaxInfoList[i];
        if (typeof taxInfo.newInnerUUID !== 'string' || taxInfo.newInnerUUID === '') {
            throw new Error('Invoice tax UUID is required');
        }
        invoiceTaxInfoMap.set(taxInfo.newInnerUUID, taxInfo);
    }

    const taxRateMap = new Map();
    invoiceTaxInfoMap.forEach((taxInfo, taxUUID) => {
        taxRateMap.set(taxUUID, 0);
    });
    for (let i = 0; i < itemTaxInfoList.length; i++) {
        const itemTaxInfo = itemTaxInfoList[i];
        if (!invoiceTaxInfoMap.has(itemTaxInfo.newInnerUUID)) {
            throw new Error('Item tax does not belong to the invoice: ' + itemTaxInfo.newInnerUUID);
        }
        if (itemTaxInfo.chooseRate === '-1' || itemTaxInfo.chooseRate === -1) {
            taxRateMap.set(itemTaxInfo.newInnerUUID, 0);
            continue;
        }
        const taxRate = Number(itemTaxInfo.chooseRate);
        if (!Number.isFinite(taxRate)) {
            throw new Error('Item tax rate is invalid: ' + itemTaxInfo.newInnerUUID);
        }
        taxRateMap.set(itemTaxInfo.newInnerUUID, taxRate);
    }

    const factorMap = new Map();
    const calculatingTaxUUIDSet = new Set();

    function calculateFactor(taxUUID) {
        if (factorMap.has(taxUUID)) {
            return factorMap.get(taxUUID);
        }
        if (calculatingTaxUUIDSet.has(taxUUID)) {
            throw new Error('Circular cumulative tax relationship: ' + taxUUID);
        }
        const taxInfo = invoiceTaxInfoMap.get(taxUUID);
        if (taxInfo === undefined) {
            throw new Error('Cumulative tax is missing: ' + taxUUID);
        }

        calculatingTaxUUIDSet.add(taxUUID);
        const taxInclusive = Number(taxInfo.taxInclusive) === 1;
        const dependencyUUIDList = parseTaxUUIDList(taxInfo.accumulateTaxInfoUUIDArrayKey);
        let dependencyFactor = 0;
        for (let i = 0; i < dependencyUUIDList.length; i++) {
            const dependencyTaxInfo = invoiceTaxInfoMap.get(dependencyUUIDList[i]);
            if (dependencyTaxInfo === undefined) {
                throw new Error('Cumulative tax is missing: ' + dependencyUUIDList[i]);
            }
            if (!taxInclusive || Number(dependencyTaxInfo.taxInclusive) === 1) {
                dependencyFactor += calculateFactor(dependencyUUIDList[i]);
            }
        }

        const taxFactor = taxRateMap.get(taxUUID) * (1 + dependencyFactor);
        calculatingTaxUUIDSet.delete(taxUUID);
        factorMap.set(taxUUID, taxFactor);
        return taxFactor;
    }

    invoiceTaxInfoMap.forEach((taxInfo, taxUUID) => {
        calculateFactor(taxUUID);
    });
    return factorMap;
}
